import {
  Flex,
  Accordion,
  FileUploadButton,
  MultipleFileUpload,
  Text,
  Input,
  InputDescriptionWrap,
} from 'components'
import { Button } from '@nextretreat/ui-components/dist/Button'
import styled from '@emotion/styled'
import { rem } from 'polished'
import { FieldArray, useFormikContext } from 'formik'
import { IVenueFormValues } from './FormPage'
import { EmptyDiv } from './GeneralInfoStep'
import get from 'lodash.get'
import { IRoom, mealTypes } from 'utils'
import { inputDescriptions } from './inputDescriptions'

const StyledFlex = styled(Flex)`
  flex-direction: column;
  gap: ${rem(20)};
`

const StyledFlexWithMargin = styled(StyledFlex)`
  margin-top: ${rem(20)};
  padding: 0 5px;
`

const Row = styled(Flex)`
  gap: ${rem(10)};

  & > * {
    flex-grow: 1;
    flex-basis: 100%;
  }
`

export const RoomsStep = () => {
  const { values, setFieldValue } = useFormikContext<IVenueFormValues>()

  const rooms = values.rooms as IRoom[]

  return (
    <>
      <Text fontSize={18} fontWeight="500" textAlign="left">
        Specify the details of each room inside your property.
      </Text>
      <Text fontSize={14} textAlign="left">
        If you want to add more rooms – use the button “Add room”. Also, please
        make sure to upload at least one photo for each room.
      </Text>

      <FieldArray name="rooms">
        {({ remove, push }) => (
          <StyledFlex>
            {rooms?.length > 0 &&
              rooms.map(({ title }: { title: string }, index: number) => {
                const roomName = `rooms.${index}`

                const roomCapacity = get(values, `${roomName}.capacity`)

                return (
                  <Accordion
                    key={index}
                    isInitialOpen={index === 0}
                    title={`Accommodation - Bedroom ${index + 1}${
                      title ? ' - ' + title : ''
                    }`}
                  >
                    <StyledFlexWithMargin>
                      <InputDescriptionWrap text={inputDescriptions.rooms.name}>
                        <Row>
                          <Input
                            label="Title"
                            placeholder="Enter title"
                            name={`${roomName}.title`}
                          />

                          <EmptyDiv />
                        </Row>
                      </InputDescriptionWrap>

                      <InputDescriptionWrap
                        text={inputDescriptions.rooms.description}
                      >
                        <Input.Textarea
                          isBlock
                          label="Description"
                          placeholder="Describe the room"
                          name={`${roomName}.description`}
                        />
                      </InputDescriptionWrap>

                      <InputDescriptionWrap text={inputDescriptions.rooms.size}>
                        <Input
                          label="Room size"
                          isBlock
                          type="number"
                          min={0}
                          name={`${roomName}.squareMeters`}
                          suffix="m²"
                        />
                      </InputDescriptionWrap>

                      <InputDescriptionWrap
                        text={inputDescriptions.rooms.quantity}
                      >
                        <Input.Number
                          min={0}
                          label="Quantity"
                          name={`${roomName}.quantity`}
                          value={get(values, `${roomName}.quantity`)}
                          onChange={(val) =>
                            setFieldValue(`${roomName}.quantity`, val)
                          }
                        />
                      </InputDescriptionWrap>

                      <InputDescriptionWrap
                        text={inputDescriptions.rooms.capacity}
                      >
                        <Input.Number
                          min={0}
                          label="Maximum capacity"
                          name={`${roomName}.capacity`}
                          value={get(values, `${roomName}.capacity`)}
                          onChange={(val) =>
                            setFieldValue(`${roomName}.capacity`, val)
                          }
                        />
                      </InputDescriptionWrap>

                      <InputDescriptionWrap
                        text={inputDescriptions.rooms.thumbnail}
                      >
                        <FileUploadButton
                          name={`${roomName}.thumbnail`}
                          fileObj={get(values, `${roomName}.thumbnail`)}
                          onSubmit={(file) =>
                            setFieldValue(`${roomName}.thumbnail`, file)
                          }
                        />
                      </InputDescriptionWrap>

                      <InputDescriptionWrap
                        text={inputDescriptions.rooms.photos}
                      >
                        <FieldArray name={`${roomName}.photos`}>
                          {({ remove: removePhoto, push: pushPhoto }) => (
                            <MultipleFileUpload
                              name={`${roomName}.photos`}
                              onRemoveFile={removePhoto}
                              onDrop={(acceptedFiles) =>
                                acceptedFiles.forEach((file) =>
                                  pushPhoto({ file })
                                )
                              }
                            />
                          )}
                        </FieldArray>
                      </InputDescriptionWrap>

                      <Flex flexDirection="row-reverse">
                        <Button.Secondary onClick={() => remove(index)}>
                          Remove Room {index + 1}
                        </Button.Secondary>
                      </Flex>

                      {!values.generalInfo.isEntireHouse && (
                        <FieldArray name={`${roomName}.variations`}>
                          {({
                            remove: removeVariation,
                            push: pushVariation,
                          }) => (
                            <StyledFlex>
                              {rooms[index].variations?.length > 0 &&
                                rooms[index].variations.map(
                                  (_: unknown, variationIndex: number) => {
                                    const variationName = `${roomName}.variations.${variationIndex}`

                                    return (
                                      <Accordion
                                        key={variationIndex}
                                        isInitialOpen={
                                          index === 0 && variationIndex === 0
                                        }
                                        title={`Variation ${
                                          variationIndex + 1
                                        }`}
                                      >
                                        <StyledFlexWithMargin>
                                          <InputDescriptionWrap
                                            text={
                                              inputDescriptions.rooms.variations
                                                .capacity
                                            }
                                          >
                                            <Input.Number
                                              label="Capacity"
                                              max={roomCapacity}
                                              min={0}
                                              name={`${variationName}.capacity`}
                                              value={get(
                                                values,
                                                `${variationName}.capacity`
                                              )}
                                              onChange={(val) =>
                                                setFieldValue(
                                                  `${variationName}.capacity`,
                                                  val
                                                )
                                              }
                                            />
                                          </InputDescriptionWrap>

                                          <InputDescriptionWrap
                                            text={
                                              inputDescriptions.rooms.variations
                                                .meal
                                            }
                                          >
                                            <Input.Select
                                              isBlock
                                              label="Meal Type"
                                              placeholder="Select meal type"
                                              onChange={(val) =>
                                                setFieldValue(
                                                  `${variationName}.meal`,
                                                  val
                                                )
                                              }
                                              name={`${variationName}.meal`}
                                              value={get(
                                                values,
                                                `${variationName}.meal`
                                              )}
                                              options={Object.keys(
                                                mealTypes
                                              ).map((type) => ({
                                                // @ts-ignore
                                                label: mealTypes[type],
                                                value: type,
                                              }))}
                                            />
                                          </InputDescriptionWrap>

                                          <InputDescriptionWrap
                                            text={
                                              inputDescriptions.rooms.variations
                                                .description
                                            }
                                          >
                                            <Input
                                              isBlock
                                              name={`${variationName}.description`}
                                              label="Description"
                                              placeholder="Enter description"
                                            />
                                          </InputDescriptionWrap>

                                          <InputDescriptionWrap
                                            text={
                                              inputDescriptions.rooms.variations
                                                .lowRate
                                            }
                                          >
                                            <Input
                                              isBlock
                                              name={`${variationName}.lowRate`}
                                              type="number"
                                              label="Lowest Price"
                                              min={0}
                                              suffix="€"
                                              placeholder="Enter lowest price"
                                            />
                                          </InputDescriptionWrap>

                                          <Flex flexDirection="row-reverse">
                                            <Button.Secondary
                                              onClick={() =>
                                                removeVariation(variationIndex)
                                              }
                                            >
                                              Remove Variation{' '}
                                              {variationIndex + 1}
                                            </Button.Secondary>
                                          </Flex>
                                        </StyledFlexWithMargin>
                                      </Accordion>
                                    )
                                  }
                                )}

                              <InputDescriptionWrap
                                text={inputDescriptions.rooms.variations.add}
                              >
                                <Button.Primary
                                  type="button"
                                  onClick={() =>
                                    pushVariation({
                                      description: '',
                                    })
                                  }
                                >
                                  Add variation
                                  {rooms[index].variations?.length === 0
                                    ? ' (required)'
                                    : ''}
                                </Button.Primary>
                              </InputDescriptionWrap>
                            </StyledFlex>
                          )}
                        </FieldArray>
                      )}
                    </StyledFlexWithMargin>
                  </Accordion>
                )
              })}

            <div>
              <Button.Primary
                type="button"
                onClick={() => push({ title: '', description: '' })}
              >
                Add room
              </Button.Primary>
            </div>
          </StyledFlex>
        )}
      </FieldArray>
    </>
  )
}
